import { securityUrls } from './account/security'
import {
  AccountRelatedPath,
  getAccountRelatedUrl,
  PaginationQueryParams,
  server,
} from '@/config/urls'
import { composePathAndParams, joinValues } from '@/utils/urls'

import { ProjectType } from '@/types/index.d'

export const accountUrls = {
  getAccount: getAccountRelatedUrl,
  getAccounts: joinValues(server, 'workspaces'),
  getLeadTracking: (workspaceId: string) =>
    joinValues(getAccountRelatedUrl(workspaceId), 'lead_tracking'),
  getConnector: ({
    workspaceId,
    connectorId,
  }: AccountRelatedPath & {
    connectorId: string
  }): string =>
    joinValues(getAccountRelatedUrl(workspaceId), 'connections', connectorId),
  getConnectors: ({
    workspaceId,
    params,
  }: AccountRelatedPath & {
    params?: PaginationQueryParams & { types?: string }
  }): string => {
    const path = joinValues(getAccountRelatedUrl(workspaceId), 'connections')
    return composePathAndParams(path, params)
  },
  getConnectorLookups: ({
    workspaceId,
    connectorId,
    params,
  }: AccountRelatedPath & {
    connectorId: string
    params?: PaginationQueryParams & { types?: string }
  }): string => {
    const path = joinValues(
      accountUrls.getConnectors({
        workspaceId,
      }),
      connectorId,
      'lookup'
    )
    return composePathAndParams(path, params)
  },
  getConnectorTest: ({
    workspaceId,
    connectorId,
  }: AccountRelatedPath & { connectorId: string }): string =>
    joinValues(
      accountUrls.getConnectors({
        workspaceId,
      }),
      connectorId,
      'test'
    ),
  security: securityUrls,
  getPing: ({ workspaceId }: AccountRelatedPath): string =>
    joinValues(getAccountRelatedUrl(workspaceId), 'ping'),
  getProjects: ({
    workspaceId,
    params,
  }: AccountRelatedPath & { params?: PaginationQueryParams }): string => {
    const path = joinValues(getAccountRelatedUrl(workspaceId), 'projects')
    return composePathAndParams(path, params)
  },
  getUser: ({
    workspaceId,
    userId,
  }: AccountRelatedPath & { userId: string }): string =>
    joinValues(getAccountRelatedUrl(workspaceId), 'users', userId),
  getUserEmailVerificationUrl: ({
    workspaceId,
    userId,
  }: AccountRelatedPath & { userId: string }): string =>
    joinValues(
      accountUrls.getUser({
        workspaceId,
        userId,
      }),
      'send_verification'
    ),
  getUsers: ({
    workspaceId,
    params,
  }: AccountRelatedPath & {
    params?: PaginationQueryParams & { statuses?: string }
  }): string => {
    const path = joinValues(getAccountRelatedUrl(workspaceId), 'users')
    return composePathAndParams(path, params)
  },
  getWorkspaceNotifications: ({
    workspaceId,
    params,
  }: AccountRelatedPath & {
    params?: PaginationQueryParams & { dismissed?: boolean; read?: boolean }
  }): string => {
    const path = joinValues(
      getAccountRelatedUrl(workspaceId),
      'user/notifications'
    )
    return composePathAndParams(path, params)
  },
  getWorkspaceNotificationDismiss: ({
    workspaceId,
    notificationId,
  }: AccountRelatedPath & {
    params?: PaginationQueryParams
    notificationId: string
  }): string =>
    joinValues(
      accountUrls.getWorkspaceNotifications({ workspaceId }),
      notificationId,
      'dismiss'
    ),
  getWorkspaceNotificationRead: ({
    workspaceId,
    notificationId,
  }: AccountRelatedPath & { notificationId: string }): string =>
    joinValues(
      accountUrls.getWorkspaceNotifications({ workspaceId }),
      notificationId,
      'read'
    ),
  getWorkspaceNotificationsRead: ({
    workspaceId,
  }: AccountRelatedPath): string =>
    joinValues(accountUrls.getWorkspaceNotifications({ workspaceId }), 'read'),
  getExportUsersUrl: ({
    workspaceId,
    params,
  }: AccountRelatedPath & { params?: any }): string => {
    const path = joinValues(getAccountRelatedUrl(workspaceId), 'users/export')
    return composePathAndParams(path, params)
  },
  getProjectLicensesUrl: ({
    workspaceId,
    params,
  }: AccountRelatedPath & {
    params: { type: ProjectType }
  }): string => {
    const path = joinValues(
      getAccountRelatedUrl(workspaceId),
      'project_licenses'
    )
    return composePathAndParams(path, params)
  },
  getSubscription: ({ workspaceId }: AccountRelatedPath): string =>
    joinValues(getAccountRelatedUrl(workspaceId), 'subscription'),
  getUserLicensesUrl: ({ workspaceId }: AccountRelatedPath): string =>
    joinValues(getAccountRelatedUrl(workspaceId), `user_licenses`),
  getUserProjectsUrl: ({
    workspaceId,
    userId,
  }: AccountRelatedPath & { userId: string }): string =>
    joinValues(accountUrls.getUser({ workspaceId, userId }), 'projects'),
  getUsageUrl: ({ workspaceId }: AccountRelatedPath): string =>
    joinValues(getAccountRelatedUrl(workspaceId), `usage`),
}
